import { useState, useRef, useCallback } from "react";
import { Share2, MessageCircle, Instagram, Copy, Download, X, Check } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { trackShare } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = window.location.origin;

// Generate a vote card image on canvas
const generateVoteCard = (product, zcs) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const w = 1080, h = 1350;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, w, h);

    // Top accent bar
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, "#5B6BA8");
    grad.addColorStop(1, "#7B8BC8");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, 8);

    // Brand
    ctx.fillStyle = "#5B6BA8";
    ctx.font = "bold 36px 'Poppins', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("zeel.beauty", w / 2, 70);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "18px 'Poppins', sans-serif";
    ctx.fillText("Community-Validated Beauty Reviews", w / 2, 100);

    // Divider
    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(80, 130);
    ctx.lineTo(w - 80, 130);
    ctx.stroke();

    // Product name
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 42px 'Poppins', sans-serif";
    ctx.textAlign = "center";
    const productName = product.name || "Product";
    // Word wrap
    const words = productName.split(" ");
    let line = "";
    let y = 200;
    const maxWidth = w - 160;
    for (const word of words) {
      const test = line + word + " ";
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line.trim(), w / 2, y);
        line = word + " ";
        y += 52;
      } else {
        line = test;
      }
    }
    ctx.fillText(line.trim(), w / 2, y);
    y += 70;

    // Trust Score Circle
    const score = zcs?.zcs || 0;
    const displayReady = zcs?.display_ready;

    if (displayReady && score) {
      const cx = w / 2, cy = y + 110, r = 90;

      // Background circle
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 12;
      ctx.stroke();

      // Score arc
      const angle = (score / 100) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, -Math.PI / 2, angle);
      ctx.strokeStyle = "#5B6BA8";
      ctx.lineWidth = 12;
      ctx.lineCap = "round";
      ctx.stroke();

      // Score number
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 64px 'Poppins', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(score), cx, cy);

      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#64748b";
      ctx.font = "20px 'Poppins', sans-serif";
      ctx.fillText("Community Confidence Score", cx, cy + r + 40);

      // Interpretation
      let interp = "Still Building Data";
      let interpColor = "#64748b";
      if (score >= 70) { interp = "High Community Confidence"; interpColor = "#0d9488"; }
      else if (score >= 50) { interp = "Moderate Community Confidence"; interpColor = "#5B6BA8"; }
      else if (score >= 30) { interp = "Low Community Confidence"; interpColor = "#d97706"; }
      else { interp = "Community Caution"; interpColor = "#e11d48"; }

      ctx.fillStyle = interpColor;
      ctx.font = "bold 24px 'Poppins', sans-serif";
      ctx.fillText(interp, cx, cy + r + 75);

      y = cy + r + 120;
    } else {
      ctx.fillStyle = "#64748b";
      ctx.font = "24px 'Poppins', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Still Building Data", w / 2, y + 60);
      y += 120;
    }

    // Vote breakdown
    if (zcs?.breakdown) {
      y += 20;
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 24px 'Poppins', sans-serif";
      ctx.textAlign = "left";

      // Effectiveness
      if (zcs.breakdown.effectiveness?.length > 0) {
        ctx.fillStyle = "#0d9488";
        ctx.fillText("What Worked", 100, y);
        y += 10;

        for (const item of zcs.breakdown.effectiveness.slice(0, 3)) {
          y += 42;
          // Bar background
          ctx.fillStyle = "#f1f5f9";
          roundRect(ctx, 100, y - 22, w - 200, 32, 6);
          ctx.fill();
          // Bar fill
          ctx.fillStyle = "#0d948830";
          roundRect(ctx, 100, y - 22, (w - 200) * (item.percentage / 100), 32, 6);
          ctx.fill();
          // Text
          ctx.fillStyle = "#334155";
          ctx.font = "16px 'Poppins', sans-serif";
          ctx.textAlign = "left";
          ctx.fillText(item.text, 112, y);
          ctx.textAlign = "right";
          ctx.font = "bold 16px 'Poppins', sans-serif";
          ctx.fillStyle = "#0d9488";
          ctx.fillText(`${item.percentage}%`, w - 112, y);
        }
        y += 20;
      }

      // Risks
      if (zcs.breakdown.risks?.length > 0) {
        y += 15;
        ctx.textAlign = "left";
        ctx.fillStyle = "#e11d48";
        ctx.font = "bold 24px 'Poppins', sans-serif";
        ctx.fillText("Reported Issues", 100, y);
        y += 10;

        for (const item of zcs.breakdown.risks.slice(0, 2)) {
          y += 42;
          ctx.fillStyle = "#f1f5f9";
          roundRect(ctx, 100, y - 22, w - 200, 32, 6);
          ctx.fill();
          ctx.fillStyle = "#e11d4830";
          roundRect(ctx, 100, y - 22, (w - 200) * (item.percentage / 100), 32, 6);
          ctx.fill();
          ctx.fillStyle = "#334155";
          ctx.font = "16px 'Poppins', sans-serif";
          ctx.textAlign = "left";
          ctx.fillText(item.text, 112, y);
          ctx.textAlign = "right";
          ctx.font = "bold 16px 'Poppins', sans-serif";
          ctx.fillStyle = "#e11d48";
          ctx.fillText(`${item.percentage}%`, w - 112, y);
        }
      }
    }

    // Footer
    const fy = h - 80;
    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(80, fy);
    ctx.lineTo(w - 80, fy);
    ctx.stroke();

    ctx.fillStyle = "#64748b";
    ctx.font = "18px 'Poppins', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("See full reviews and vote at", w / 2, fy + 35);
    ctx.fillStyle = "#5B6BA8";
    ctx.font = "bold 22px 'Poppins', sans-serif";
    ctx.fillText("zeel.beauty", w / 2, fy + 65);

    canvas.toBlob((blob) => resolve(blob), "image/png", 1.0);
  });
};

// Rounded rect helper
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Share Modal Component
export const ShareModal = ({ product, zcs, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const productUrl = `${SITE_URL}/product/${product.slug || product.product_id}`;
  const shareUrl = `${API_URL}/api/og/product/${product.slug || product.product_id}`;
  const shareText = `Check out ${product.name} on Zeel — Community Confidence: ${zcs?.zcs || "N/A"}%. See real community reviews and vote!`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackShare(product.product_id, "copy_link");
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const shareWhatsApp = () => {
    trackShare(product.product_id, "whatsapp");
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
    window.open(url, "_blank");
  };

  const downloadVoteCard = async () => {
    setGenerating(true);
    try {
      const blob = await generateVoteCard(product, zcs);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zeel-${product.name.replace(/\s+/g, "-").toLowerCase()}-trust-score.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vote card downloaded! Share it on Instagram.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate card");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-sm w-full p-6 shadow-2xl animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold">Share</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-share-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {/* WhatsApp */}
          <button
            onClick={shareWhatsApp}
            className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
            data-testid="share-whatsapp"
          >
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">WhatsApp</p>
              <p className="text-xs text-muted-foreground">Share with friends</p>
            </div>
          </button>

          {/* Instagram - Share Link */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(shareUrl).then(() => {
                setCopied(true);
                trackShare(product.product_id, "instagram");
                toast.success("Link copied! Paste it on Instagram.");
                setTimeout(() => setCopied(false), 2000);
              }).catch(() => toast.error("Couldn't copy link"));
            }}
            className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
            data-testid="share-instagram"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">Instagram</p>
              <p className="text-xs text-muted-foreground">Copy link to share on Instagram</p>
            </div>
          </button>

          {/* Copy Link */}
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
            data-testid="share-copy-link"
          >
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-slate-600" />}
            </div>
            <div>
              <p className="font-medium text-sm">{copied ? "Copied!" : "Copy Link"}</p>
              <p className="text-xs text-muted-foreground">Share anywhere</p>
            </div>
          </button>
        </div>

        {/* Download Trust Card — separate action */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={downloadVoteCard}
            disabled={generating}
            className="w-full flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left"
            data-testid="download-trust-card"
          >
            <div className="w-10 h-10 rounded-full bg-[#5B6BA8] flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">{generating ? "Generating..." : "Download Trust Card"}</p>
              <p className="text-xs text-muted-foreground">Save as image for Stories or posts</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Small share button for product cards
export const ShareButton = ({ product, zcs, className = "" }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowModal(true); }}
        className={`p-1.5 rounded-full hover:bg-slate-100 transition-colors ${className}`}
        data-testid={`share-btn-${product.product_id}`}
        title="Share"
      >
        <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      {showModal && <ShareModal product={product} zcs={zcs} onClose={() => setShowModal(false)} />}
    </>
  );
};

export default ShareModal;
